import { useState, useMemo, useId, useEffect, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, Download, LayoutList, Trash2 } from 'lucide-react';

function rowKeyForList(row, listSource) {
  if (listSource === 'timeCardsResource') return String(row?.timeCardId ?? '').trim();
  return String(row?.timeRecordGroupId ?? row?.timeCardId ?? '').trim();
}

function isoDateDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIsoUtc() {
  return new Date().toISOString().slice(0, 10);
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

export default function TimeCardsListPanel({
  api,
  user,
  connectionsList = [],
  selectedConnectionId,
  onSelectConnectionId,
  onRequestOracleLogin,
  listPrefill = null,
  onListPrefillConsumed = null,
  onOpenDeleteWithRange = null,
}) {
  const formId = useId();
  const hasCredentials = !!(user || (connectionsList && connectionsList.length > 0));

  const activeConnectionId = useMemo(() => {
    if (user) return null;
    if (!connectionsList?.length) return null;
    if (selectedConnectionId && connectionsList.some((c) => c.id === selectedConnectionId)) {
      return selectedConnectionId;
    }
    return connectionsList[0]?.id ?? null;
  }, [user, connectionsList, selectedConnectionId]);

  const [personNumber, setPersonNumber] = useState('');
  const [startDate, setStartDate] = useState(() => isoDateDaysAgo(13));
  const [endDate, setEndDate] = useState(todayIsoUtc);
  /** Default matches payroll "Time record groups" — not GET /timeCards (ViewCriteria may 400 on some pods). */
  const [listSource, setListSource] = useState('timeRecordGroups');
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const headerSelectRef = useRef(null);

  useEffect(() => {
    if (!listPrefill) return;
    if (listPrefill.startDate) setStartDate(listPrefill.startDate);
    if (listPrefill.endDate) setEndDate(listPrefill.endDate);
    if (listPrefill.personNumber) setPersonNumber(String(listPrefill.personNumber));
    onListPrefillConsumed?.();
  }, [listPrefill, onListPrefillConsumed]);

  const runFetch = useCallback(async () => {
    setError(null);
    setResult(null);
    if (!hasCredentials) {
      setError('Connect to Oracle or add a saved connection first.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      setError('Choose a valid start and end date.');
      return;
    }
    setLoading(true);
    try {
      const body = { startDate, endDate, maxRows: 100000, listSource };
      const pn = personNumber.trim();
      if (pn) body.personNumber = pn;
      if (!user && activeConnectionId) body.connectionId = activeConnectionId;
      const res = await api.post('/api/hcm-timecards/fetch', body, { timeout: 600000 });
      setResult(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Request failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [api, hasCredentials, startDate, endDate, personNumber, user, activeConnectionId, listSource]);

  const rowKeys = useMemo(() => {
    if (!result?.rows?.length) return [];
    const src = result.listSource;
    return result.rows.map((r) => rowKeyForList(r, src)).filter((k) => k !== '');
  }, [result]);

  const allRowsSelected = rowKeys.length > 0 && rowKeys.every((k) => selectedIds.has(k));
  const someRowsSelected = rowKeys.some((k) => selectedIds.has(k));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (el) el.indeterminate = someRowsSelected && !allRowsSelected;
  }, [someRowsSelected, allRowsSelected]);

  const toggleRow = (key) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (allRowsSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rowKeys));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0 || !result?.listSource) return;
    const n = selectedIds.size;
    if (!window.confirm(`Delete ${n} row(s) in Oracle HCM? This cannot be undone.`)) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const src = result.listSource;
      const items = [...selectedIds].map((id) => {
        if (src === 'timeCardsResource') return { timeCardId: id };
        return { timeRecordGroupId: id };
      });
      const body = { confirm: true, listSource: src, items };
      if (!user && activeConnectionId) body.connectionId = activeConnectionId;
      const res = await api.post('/api/hcm-timecards/list-selection-delete', body, { timeout: 600000 });
      const { results, okCount, failCount } = res.data;
      const successKeys = new Set();
      for (const r of results || []) {
        if (r.ok) {
          if (src === 'timeCardsResource' && r.timeCardId) successKeys.add(String(r.timeCardId));
          if (src !== 'timeCardsResource' && r.timeRecordGroupId) successKeys.add(String(r.timeRecordGroupId));
        }
      }
      setResult((prev) => {
        if (!prev?.rows) return prev;
        const nextRows = prev.rows.filter((row) => !successKeys.has(rowKeyForList(row, prev.listSource)));
        return { ...prev, rows: nextRows, rowCount: nextRows.length };
      });
      setSelectedIds(new Set());
      if (failCount > 0) {
        setError(
          `Deleted ${okCount} of ${items.length}. ${failCount} failed (check Oracle permissions or whether the row was already removed).`
        );
      } else {
        setError(null);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Delete failed.';
      setError(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const exportCsv = () => {
    if (!result?.rows?.length) return;
    const slug = result.personNumber || 'all-persons';
    const rows = result.rows.map((r) => ({
      timeRecordGroupId: r.timeRecordGroupId != null && String(r.timeRecordGroupId).trim() ? r.timeRecordGroupId : '',
      timeCardId: r.timeCardId || '',
      personNumber: r.personNumber,
      lineCount: r.lineCount,
      totalHours: formatHours(r.totalHours),
      startTime: r.startTime,
      stopTime: r.stopTime,
    }));
    downloadCsv(`hcm-time-cards-${slug}-${result.startDate}_${result.endDate}.csv`, rows);
  };

  const goDelete = () => {
    if (!onOpenDeleteWithRange) return;
    const pn = personNumber.trim();
    onOpenDeleteWithRange({
      personNumber: pn,
      startDate,
      stopDate: endDate,
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 text-white shadow-md">
          <LayoutList className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-slate-800">HCM time cards (list)</h1>
          <p className="mt-1 text-sm text-slate-600">
            <span className="font-medium">Default</span> uses the same <span className="font-mono">timeRecordGroups</span> + child
            timeRecords path as <span className="font-medium">Payroll hours</span>. Deleting here removes the time record group (or its
            child time records if Oracle disallows group DELETE). <span className="font-mono">GET /timeCards</span> list source deletes
            the whole time card. Use the separate <span className="font-medium">Delete HCM time cards</span> flow for line-level
            delete by time.
          </p>
        </div>
      </div>

      {!hasCredentials && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-700" />
            Sign in to Oracle or create a connection with HCM URL and credentials.
          </span>
          <button
            type="button"
            onClick={onRequestOracleLogin}
            className="shrink-0 rounded-lg bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800"
          >
            Oracle sign-in
          </button>
        </div>
      )}

      <div className="card-panel p-6 shadow-sm">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            runFetch();
          }}
        >
          {!user && connectionsList.length > 0 && (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">Connection</label>
              {connectionsList.length === 1 ? (
                <p className="text-sm text-slate-700">{connectionsList[0].connection_name || 'Saved connection'}</p>
              ) : (
                <select
                  value={activeConnectionId || ''}
                  onChange={(e) => onSelectConnectionId(e.target.value || null)}
                  className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  {connectionsList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.connection_name || c.application_hcm_url || 'Unnamed'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            <label htmlFor={`${formId}-src`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Source
            </label>
            <select
              id={`${formId}-src`}
              value={listSource}
              onChange={(e) => setListSource(e.target.value)}
              className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              <option value="timeRecordGroups">Time record groups (like payroll) — recommended</option>
              <option value="timeCardsResource">GET /timeCards resource (may error on this pod)</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor={`${formId}-pn`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Person number (optional — all in range if blank, when the pod allows)
            </label>
            <input
              id={`${formId}-pn`}
              type="text"
              value={personNumber}
              onChange={(e) => setPersonNumber(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder="Leave empty for all employees in range (may not work on all Oracle builds)"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor={`${formId}-start`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Start date
            </label>
            <input
              id={`${formId}-start`}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm"
            />
          </div>
          <div>
            <label htmlFor={`${formId}-end`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              End date
            </label>
            <input
              id={`${formId}-end`}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm"
            />
          </div>

          <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={loading || !hasCredentials}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:from-cyan-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LayoutList className="h-4 w-4" />}
              Fetch time cards
            </button>
            {onOpenDeleteWithRange && (
              <button
                type="button"
                onClick={goDelete}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
              >
                <Trash2 className="h-4 w-4 text-slate-600" />
                Open delete tool with this range
              </button>
            )}
          </div>
        </form>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result?.success && (
        <div className="card-panel overflow-hidden p-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <p className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">{result.rowCount}</span>{' '}
              {result.listSource === 'timeRecordGroups' ? (
                <>time record group{result.rowCount !== 1 ? 's' : ''} (list)</>
              ) : (
                <>time card{result.rowCount !== 1 ? 's' : ''}</>
              )}
              {result.detailLineCount != null && result.listSource === 'timeRecordGroups' && (
                <>
                  {' '}
                  · {result.detailLineCount} line row{result.detailLineCount !== 1 ? 's' : ''} with positive work time
                  {result.detailLineCountBeforeHourFilter != null &&
                    result.detailLineCountBeforeHourFilter > result.detailLineCount && (
                    <>
                      {' '}
                      (of {result.detailLineCountBeforeHourFilter} before work-time filter)
                    </>
                  )}
                </>
              )}
              {result.timeRecordGroupCount != null &&
                result.listSource === 'timeRecordGroups' &&
                result.timeRecordGroupCount > result.rowCount && (
                  <>
                    {' '}
                    · {result.timeRecordGroupCount} group{result.timeRecordGroupCount !== 1 ? 's' : ''} in Oracle range;{' '}
                    {result.groupsWithNoPositiveHours ?? result.timeRecordGroupCount - result.rowCount} omitted (no
                    line with positive work time)
                  </>
                )}
              {result.personNumber ? (
                <>
                  {' '}
                  · Person <span className="font-mono">{result.personNumber}</span>
                </>
              ) : (
                ' · All persons (date range)'
              )}{' '}
              · <span className="font-mono text-xs">{result.startDate}</span> → <span className="font-mono text-xs">{result.endDate}</span>
              {result.listMayBeTruncated ? <span className="text-amber-800"> · Capped (increase maxRows in API body if needed)</span> : null}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={deleteSelected}
                disabled={!result.rows?.length || selectedIds.size === 0 || deleteLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Delete selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </button>
              <button
                type="button"
                onClick={exportCsv}
                disabled={!result.rows?.length}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                CSV
              </button>
            </div>
          </div>
          {(result.hcmHostname || result.oracleUsernameHint || result.credentialSource) && (
            <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-800">HCM REST</span>
              {result.credentialSource ? (
                <>
                  {' '}
                  · source: <span className="font-mono">{result.credentialSource}</span>
                </>
              ) : null}
              {result.connectionName ? (
                <>
                  {' '}
                  · connection: {result.connectionName}
                </>
              ) : null}
              {result.hcmHostname ? (
                <>
                  {' '}
                  · host: <span className="font-mono break-all">{result.hcmHostname}</span>
                </>
              ) : null}
              {result.oracleUsernameHint ? (
                <>
                  {' '}
                  · user: <span className="font-mono">{result.oracleUsernameHint}</span>
                </>
              ) : null}
            </p>
          )}
          {result.listNote && (
            <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-600">{result.listNote}</p>
          )}
          {result.timeCardLineChildSegmentNote && (
            <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-600">{result.timeCardLineChildSegmentNote}</p>
          )}
          {result.fetchStrategy && (
            <p className="border-b border-slate-100 px-4 py-1 text-xs font-mono text-slate-500">{result.fetchStrategy}</p>
          )}
          <div className="max-h-[min(60vh,520px)] overflow-auto p-2">
            <table className="w-full min-w-[680px] border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-10 px-1 py-2 text-center">
                    <input
                      ref={headerSelectRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-cyan-600"
                      checked={allRowsSelected}
                      onChange={toggleSelectAll}
                      title="Select all in list"
                    />
                  </th>
                  <th className="px-2 py-2">
                    {result.listSource === 'timeRecordGroups' ? 'Time record group ID' : 'Time card ID'}
                  </th>
                  <th className="px-2 py-2">Person</th>
                  <th className="px-2 py-2 text-right">Lines</th>
                  <th className="px-2 py-2 text-right">Hours</th>
                  <th className="px-2 py-2">Start</th>
                  <th className="px-2 py-2">Stop</th>
                </tr>
              </thead>
              <tbody>
                {(result.rows || []).map((r, i) => {
                  const rk = rowKeyForList(r, result.listSource);
                  return (
                  <tr
                    key={`${i}-${rk || 'x'}`}
                    className="border-b border-slate-100"
                  >
                    <td className="px-1 py-1.5 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-cyan-600"
                        checked={rk ? selectedIds.has(rk) : false}
                        onChange={() => rk && toggleRow(rk)}
                        disabled={!rk}
                        title="Select for delete"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs break-all">
                      {r.timeRecordGroupId && String(r.timeRecordGroupId).trim()
                        ? r.timeRecordGroupId
                        : r.timeCardId || '—'}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.personNumber || '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.lineCount}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatHours(r.totalHours)}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-700">{r.startTime != null ? String(r.startTime) : '—'}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-700">{r.stopTime != null ? String(r.stopTime) : '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
